import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import GenericTable from "@components/tables/GenericTable";
import { fetchSentenceTableData } from "@store/thunks/translationThunk";
import { BASE_URL, getStatusStyle } from "@utils/constants";
import apiClient from "@/services/api/apiClient";

const SentenceTable = () => {
  const dispatch = useDispatch();
  const { fileId, sourceId, targetId } = useParams();
  const translatorAdminId = useSelector((state) => state.auth?.userId);
  const { fileName } = useSelector((state) => state.fileMeta);
  const { targetLanguageName } = useSelector((state) => state.fileLanguage);
  const status = useSelector((state) => state.translatorAdmin.fileStatus);
  const token = useSelector((state) => state.auth?.token);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [loadingRejected, setLoadingRejected] = useState(false);
  const [rejectedError, setRejectedError] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [allSentences, setAllSentences] = useState([]);
  const [allRejectedSentences, setAllRejectedSentences] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      if (activeTab === "all") {
        setLoading(true);
        try {
          const res = await dispatch(
            fetchSentenceTableData({
              fileId,
              sourceId,
              targetId,
              translatorAdminId,
              status,
              pageIndex,
              pageSize,
            })
          );
          console.log("fetchSentenceTableData response:", res);
          const data = res.payload?.data || res || [];
          if (Array.isArray(data)) {
            setAllSentences(data);
            const totalPages = res.payload?.totalPages || Math.ceil(res.payload?.totalItems / pageSize) || Infinity;
            setHasNextPage(pageIndex + 1 < totalPages || data.length === pageSize);
          } else {
            setAllSentences([]);
            setHasNextPage(false);
          }
        } catch (error) {
          console.error("Error fetching sentences:", error);
          setAllSentences([]);
          setHasNextPage(false);
        } finally {
          setLoading(false);
        }
      } else if (activeTab === "rejected") {
        setLoadingRejected(true);
        setRejectedError("");
        try {
          const data = await apiClient(
            `${BASE_URL}api/translatorAdmin/rejectedSentences?fileId=${fileId}&page=${pageIndex}&size=${pageSize}`,
            {
              credentials: "include",
              headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
              },
            }, navigate
          );
          console.log("fetchRejected response:", data);
          const sentences = data.data || [];
          if (Array.isArray(sentences)) {
            setAllRejectedSentences(sentences);
            const totalPages = data.totalPages || Math.ceil(data.totalItems / pageSize) || Infinity;
            setHasNextPage(pageIndex + 1 < totalPages || sentences.length === pageSize);
            setPageIndex(0);
          } else {
            setAllRejectedSentences([]);
            setHasNextPage(false);
          }
        } catch (err) {
          setAllRejectedSentences([]);
          setRejectedError("Failed to fetch rejected sentences. Check console for details.");
          console.error("[Rejected] Fetch error:", err);
        } finally {
          setLoadingRejected(false);
        }
      }
    };

    if (fileId && sourceId && targetId && translatorAdminId) {
      fetchData();
    }
  }, [activeTab, fileId, sourceId, targetId, translatorAdminId, dispatch, status, pageIndex, pageSize, token]);

  // Compute paginated data
  const paginatedSentences = useMemo(() => {
    const start = pageIndex * pageSize;
    const end = start + pageSize;
    console.log("paginatedSentences:", allSentences.slice(start, end));
    return allSentences.slice(start, end);
  }, [allSentences, pageIndex, pageSize]);

  const paginatedRejectedSentences = useMemo(() => {
    const start = pageIndex * pageSize;
    const end = start + pageSize;
    console.log("paginatedRejectedSentences:", allRejectedSentences.slice(start, end));
    return allRejectedSentences.slice(start, end);
  }, [allRejectedSentences, pageIndex, pageSize]);

  // Update hasNextPage for client-side fallback
  useEffect(() => {
    const activeData = activeTab === "all" ? allSentences : allRejectedSentences;
    console.log("activeData:", activeData);
    setHasNextPage((pageIndex + 1) * pageSize < activeData.length);
  }, [allSentences, allRejectedSentences, activeTab, pageIndex, pageSize]);

  const allSentencesColumns = status === "PENDING" ? [
    {
      id: "serial_number",
      header: () => "Sr. No.",
      cell: ({ row }) => (
        <div className="text-center">
          {pageIndex * pageSize + row.index + 1}
        </div>
      ),
      cellClassName: "text-center",
    },
    {
      id: "sourceText",
      header: () => "Source Text",
      accessorKey: "cdacSentenceText",
      cell: ({ row }) => (
        <div className="max-w-md">{row.original.cdacSentenceText || "N/A"}</div>
      ),
    },
    {
      id: "gsSentenceText",
      header: () => "GA Sentence Text",
      accessorKey: "gaSentenceText",
      cell: ({ row }) => {
        console.log(row.original.gaSentenceText);
        console.log(typeof (row.original.gaSentenceText));
        const isUnchanged = row.original.gaSentenceText === "No change in CDAC source segment";
        const displayText = isUnchanged ? "No change" : row.original.gaSentenceText;
        return (
          <div
            className={`max-w-4xl px-4 py-2 text-base font-['Semantic/Strong'] line-clamp-2 hover:line-clamp-none transition-all duration-200 rounded-lg ${isUnchanged
              ? "text-gray-500 bg-blue-50 hover:bg-blue-100"
              : "text-gray-900 hover:bg-gray-50"
              }`}
            title={
              isUnchanged
                ? "No modifications made to the CDAC source segment"
                : row.original.gaSentenceText || "No GA sentence text available"
            }
            aria-label={
              isUnchanged
                ? "No modifications made to the CDAC source segment"
                : row.original.gaSentenceText || "No GA sentence text available"
            }
          >
            {displayText}
          </div>
        );
      },
    },
  ] : [
    {
      id: "serial_number",
      header: () => "Sr. No.",
      cell: ({ row }) => (
        <div className="text-center">
          {pageIndex * pageSize + row.index + 1}
        </div>
      ),
      cellClassName: "text-center",
    },
    {
      id: "sourceText",
      header: () => "CDAC Sentence Text",
      accessorKey: "cdacSentenceText",
      cell: ({ row }) => (
        <div className="max-w-md">{row.original.cdacSentenceText || "N/A"}</div>
      ),
    },
    {
      id: "gsSentenceText",
      header: () => "GA Sentence Text",
      accessorKey: "gsSentenceText",
      cell: ({ row }) => {
        console.log(row.original.gsSentenceText);
        console.log(typeof (row.original.gsSentenceText));
        const isUnchanged = row.original.gsSentenceText === "No change in CDAC source segment";
        const displayText = isUnchanged ? "No change" : row.original.gsSentenceText;
        return (
          <div
            className={`max-w-4xl px-4 py-2 text-base font-['Semantic/Strong'] line-clamp-2 hover:line-clamp-none transition-all duration-200 rounded-lg ${isUnchanged
              ? "text-gray-500 bg-blue-50 hover:bg-blue-100"
              : "text-gray-900 hover:bg-gray-50"
              }`}
            title={
              isUnchanged
                ? "No modifications made to the CDAC source segment"
                : row.original.gaSentenceText || "No GA sentence text available"
            }
            aria-label={
              isUnchanged
                ? "No modifications made to the CDAC source segment"
                : row.original.gaSentenceText || "No GA sentence text available"
            }
          >
            {displayText}
          </div>
        );
      },
    },
    {
      id: "translatedText",
      header: () => `GS Translated Text (${targetLanguageName})`,
      accessorKey: "translatedTextGs",
      cell: ({ row }) => (
        <div className="max-w-md">{row.original.translatedTextGs || "-"}</div>
      ),
    },
    {
      id: "translationStatus",
      header: () => "Translation Status",
      accessorKey: "translationStatus",
      cell: ({ row }) => (
        <span className={`px-2 py-1 rounded-full text-sm font-['Semantic/Strong'] ${getStatusStyle(row.original.translationStatus).bg} ${getStatusStyle(row.original.translationStatus).text}`}>
          {row.original.translationStatus || "N/A"}
        </span>
      ),
    },
    {
      id: "validationStatus",
      header: () => "Validation Status",
      accessorKey: "validationStatus",
      cell: ({ row }) => (
        <span className={`px-2 py-1 rounded-full text-sm font-['Semantic/Strong'] ${getStatusStyle(row.original.validationStatus).bg} ${getStatusStyle(row.original.validationStatus).text}`}>
          {row.original.validationStatus || "N/A"}
        </span>
      ),
    },
  ];

  const rejectedSentencesColumns = [
    {
      id: "serial_number",
      header: () => "Sr. No.",
      cell: ({ row }) => (
        <div className="text-center">
          {pageIndex * pageSize + row.index + 1}
        </div>
      ),
      cellClassName: "text-center",
    },
    {
      id: "sentenceText",
      header: () => "CDAC Sentence Text",
      accessorKey: "cdacSentenceText",
      cell: ({ row }) => (
        <div className="max-w-md">{row.original.cdacSentenceText || "N/A"}</div>
      ),
    },
    {
      id: "gaSentenceText",
      header: () => "GA Sentence Text",
      accessorKey: "gaSentenceText",
      cell: ({ row }) => {
        const isUnchanged = row.original.gaSentenceText === "No change in CDAC source segment";
        const displayText = isUnchanged ? "No change" : row.original.gaSentenceText || "N/A";
        return (
          <div
            className={`max-w-4xl px-4 py-2 text-base font-['Semantic/Strong'] line-clamp-2 hover:line-clamp-none transition-all duration-200 rounded-lg ${isUnchanged
              ? "text-gray-500 bg-blue-50 hover:bg-blue-100"
              : "text-gray-900 hover:bg-gray-50"
              }`}
            title={
              isUnchanged
                ? "No modifications made to the CDAC source segment"
                : row.original.gaSentenceText || "No GA sentence text available"
            }
            aria-label={
              isUnchanged
                ? "No modifications made to the CDAC source segment"
                : row.original.gaSentenceText || "No GA sentence text available"
            }
          >
            {displayText}
          </div>
        );
      },
    },
    {
      id: "rejectedBy",
      header: () => "Rejected By",
      accessorKey: "rejectedByTranslatorUserName",
      cell: ({ row }) => (
        <div className="max-w-md">{row.original.rejectedByTranslatorUserName || "N/A"}</div>
      ),
    },
    {
      id: "rejectionComment",
      header: () => "Rejection Comment",
      accessorKey: "rejectionComment",
      cell: ({ row }) => (
        <div className="max-w-md">{row.original.rejectionComment || "-"}</div>
      ),
    },
  ];

  return (
    <div className="flex min-h-[calc(100vh-4rem)] bg-gray-50/50">
      <div className="flex-1">
        <div className="w-full p-4 sm:p-6 lg:p-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <h2 className="text-xl font-['Semantic/Strong'] text-center font-bold mt-2">
              File Name: <span className="text-2xl font-['Semantic/Strong'] text-center text-[#ec7026] underline font-bold">{fileName}</span>
            </h2>
            <div className="px-4 sm:px-6 lg:px-8 py-4">
              <div className="flex items-center mb-4">
                <div className="flex border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    className={`px-4 py-2 text-sm font-['Semantic/Strong'] ${activeTab === "all"
                      ? "bg-[#ec7026] text-white"
                      : "bg-white text-[#ec7026] hover:bg-gray-50"
                      } transition-colors duration-200`}
                    onClick={() => setActiveTab("all")}
                  >
                    All Sentences
                  </button>
                  <button
                    className={`px-4 py-2 text-sm font-['Semantic/Strong'] ${activeTab === "rejected"
                      ? "bg-[#ec7026] text-white"
                      : "bg-white text-[#ec7026] hover:bg-gray-50"
                      } transition-colors duration-200`}
                    onClick={() => setActiveTab("rejected")}
                  >
                    Rejected Sentences
                  </button>
                </div>
              </div>
              {activeTab === "all" ? (
                loading ? (
                  <div className="p-6 text-center text-gray-500 font-['Semantic/Strong']">
                    <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
                    <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
                    <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
                  </div>
                ) : (
                  <div className="w-full overflow-x-auto">
                    <GenericTable
                      data={paginatedSentences}
                      columns={allSentencesColumns}
                      showPagination={true}
                      manualPagination={true}
                      pageIndex={pageIndex}
                      pageSize={pageSize}
                      hasNextPage={hasNextPage}
                      onPaginationChange={({ pageIndex, pageSize }) => {
                        setPageIndex(pageIndex);
                        setPageSize(pageSize);
                      }}
                      emptyMessage="No sentences available"
                    />
                  </div>
                )
              ) : loadingRejected ? (
                <div className="p-6 text-center text-gray-500 font-['Semantic/Strong']">
                  <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
                  <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
                  <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
                </div>
              ) : rejectedError ? (
                <div className="p-6">
                  <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg font-['Semantic/Strong']">
                    <p>{rejectedError}</p>
                    <p className="mt-2 text-sm">Please try again later or contact support.</p>
                  </div>
                </div>
              ) : (
                <div className="w-full overflow-x-auto">
                  <GenericTable
                    data={paginatedRejectedSentences}
                    columns={rejectedSentencesColumns}
                    showPagination={true}
                    manualPagination={true}
                    pageIndex={pageIndex}
                    pageSize={pageSize}
                    hasNextPage={hasNextPage}
                    onPaginationChange={({ pageIndex, pageSize }) => {
                      setPageIndex(pageIndex);
                      setPageSize(pageSize);
                    }}
                    emptyMessage="No rejected sentences"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SentenceTable;